/**
 * Compact Travellers list — Phase 15.6.
 *
 * Table layout (44 px rows, sticky header):
 *   ☐ | Avatar+Name+Email | UCC | Category pill | LTV | Trips | Last trip
 *     | Docs (3 dots) | Phone | Actions (hover-only)
 *
 * - Filter chips: All | Corporate | Family | Solo | Docs expiring
 * - Search (280 px) hits the existing /api/clients ?search= param.
 * - Bulk-select bar (Tag / Email / Delete) appears when 1+ rows checked.
 * - CSV / Excel export respects the active filter + sort.
 * - Hover-only row actions (View / WhatsApp / New Trip / Delete).
 *
 * NOTE: kept member-expression-free in JSX (`const rows = filtered;`
 * hoist before `.map`) to dodge the visual-edits Babel plugin recursion.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Eye, MessageCircle, Plus, Trash2, UserPlus, RefreshCw } from 'lucide-react';
import FilterChips from '@/components/list/FilterChips';
import BulkBar from '@/components/list/BulkBar';
import Pagination from '@/components/list/Pagination';
import ExportButton from '@/components/list/ExportButton';
import SortableHeader from '@/components/list/SortableHeader';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ----- Helpers ---------------------------------------------------------------

const fmtINR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtMonth = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};
const monthsUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  return (d.getFullYear() - today.getFullYear()) * 12 + (d.getMonth() - today.getMonth());
};

// Map category → pill tone. Spec: Family=blue, Corporate=purple, Solo=gray.
const CATEGORY_COLORS = {
  Family: 'bg-blue-50 text-blue-700 border-blue-200',
  Corporate: 'bg-purple-50 text-purple-700 border-purple-200',
  Solo: 'bg-slate-100 text-slate-700 border-slate-200',
};
const CATEGORY_DEFAULT = 'bg-slate-50 text-slate-500 border-slate-200';

// Doc-dot color logic (passport / aadhar / visa). 'visa' is a soft signal —
// we don't store a global expiry, so for now we treat it as "not uploaded"
// unless the traveller has at least one visa doc on file.
const DOC_TONE = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-400',
  red: 'bg-rose-500',
  gray: 'bg-slate-300',
};

const TravellersList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canWrite = (user?.role || '').toLowerCase() !== 'accounts';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [recalcingAll, setRecalcingAll] = useState(false);
  // Data-integrity cleanup (2026-10-06) — "Show archived" reveals
  // soft-deleted travellers (deletedAt set) so an admin can Restore one.
  const [showDeleted, setShowDeleted] = useState(false);

  // Fetch on search change (server-side ?search=); filter chips work client-side.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (showDeleted) params.set('include_deleted', 'true');
    const q = params.toString() ? `?${params.toString()}` : '';
    axios.get(`${BACKEND_URL}/api/clients${q}`, { withCredentials: true })
      .then((res) => { if (alive) setRows(showDeleted ? (res.data || []).filter((r) => r.deletedAt) : (res.data || [])); })
      .catch(() => toast.error('Failed to load travellers'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [search, showDeleted]);

  // Reset selection + page on filter / sort changes.
  useEffect(() => { setSelected(new Set()); setPage(1); }, [categoryFilter, search, sort, pageSize]);

  // Client-side category filter + docs-expiring filter.
  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return rows;
    if (categoryFilter === 'docs-expiring') {
      return rows.filter((t) => {
        const m = monthsUntil(t.passportExpiryDate);
        return m !== null && m >= 0 && m <= 6;
      });
    }
    return rows.filter((t) => (t.travelCategory || '').toLowerCase() === categoryFilter.toLowerCase());
  }, [rows, categoryFilter]);

  // Sort.
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (r) => {
      if (sort.key === 'name') return (r.fullName || '').toLowerCase();
      if (sort.key === 'ucc') return r.ucc || '';
      if (sort.key === 'ltv') return Number(r.totalBookingsValue || 0);
      if (sort.key === 'trips') return Number.isFinite(r.tripCount) ? r.tripCount : (Array.isArray(r.trips) ? r.trips.length : 0);
      if (sort.key === 'lastTrip') return r.lastTravelDate || '';
      return r[sort.key] || '';
    };
    return [...filtered].sort((a, b) => (get(a) < get(b) ? -1 : get(a) > get(b) ? 1 : 0) * dir);
  }, [filtered, sort]);

  // Paginate.
  const total = sorted.length;
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  // Category counts for chip labels.
  const counts = useMemo(() => {
    const c = { all: rows.length, corporate: 0, family: 0, solo: 0, expiring: 0 };
    for (const r of rows) {
      const cat = (r.travelCategory || '').toLowerCase();
      if (c[cat] !== undefined) c[cat] += 1;
      const m = monthsUntil(r.passportExpiryDate);
      if (m !== null && m >= 0 && m <= 6) c.expiring += 1;
    }
    return c;
  }, [rows]);

  const chips = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'Corporate', label: 'Corporate', count: counts.corporate },
    { value: 'Family', label: 'Family', count: counts.family },
    { value: 'Solo', label: 'Solo', count: counts.solo },
    { value: 'docs-expiring', label: 'Docs expiring', count: counts.expiring },
  ];

  // Export columns — flat CSV-friendly fields.
  const exportColumns = [
    { key: 'fullName', label: 'Name' },
    { key: 'ucc', label: 'UCC' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'travelCategory', label: 'Category' },
    { key: 'ltv', label: 'LTV (₹)', format: (r) => Number(r.totalBookingsValue || 0) },
    { key: 'trips', label: 'Trips', format: (r) => (Number.isFinite(r.tripCount) ? r.tripCount : (Array.isArray(r.trips) ? r.trips.length : 0)) },
    { key: 'passportNumber', label: 'Passport' },
    { key: 'passportExpiryDate', label: 'Passport expiry' },
    { key: 'panCard', label: 'PAN' },
    { key: 'aadharCard', label: 'Aadhar' },
  ];

  const toggle = (ucc) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(ucc)) next.delete(ucc);
      else next.add(ucc);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === pageRows.length) setSelected(new Set());
    else setSelected(new Set(pageRows.map((r) => r.ucc)));
  };

  const onBulkAction = (key) => {
    if (key === 'tag')   { toast.info('Bulk tag — coming soon'); return; }
    if (key === 'email') { toast.info('Bulk email — coming soon'); return; }
    if (key === 'delete') {
      const n = selected.size;
      if (!window.confirm(`Soft-delete ${n} traveller${n === 1 ? '' : 's'}? Travellers with active trips will be skipped.`)) return;
      const uccs = [...selected];
      Promise.allSettled(uccs.map((u) => axios.delete(`${BACKEND_URL}/api/clients/${u}`, { withCredentials: true })))
        .then((results) => {
          const ok = results.filter((r) => r.status === 'fulfilled').length;
          const fail = results.length - ok;
          toast.success(`Deleted ${ok}${fail ? `, ${fail} skipped (active trips)` : ''}`);
          // Refresh.
          setRows((rs) => rs.filter((r) => !selected.has(r.ucc) || fail > 0));
          setSelected(new Set());
        });
    }
  };

  const onRowDelete = async (ucc) => {
    if (!window.confirm('Archive this traveller? They disappear from the list but every record stays intact — you can restore them any time.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${ucc}`, { withCredentials: true });
      setRows((rs) => rs.filter((r) => r.ucc !== ucc));
      toast.success('Traveller archived');
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(typeof detail === 'string' ? detail : 'Archive failed');
    }
  };

  const onRowRestore = async (ucc) => {
    try {
      await axios.post(`${BACKEND_URL}/api/clients/${ucc}/restore`, {}, { withCredentials: true });
      setRows((rs) => rs.filter((r) => r.ucc !== ucc));
      toast.success('Traveller restored');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };

  // Admin one-shot LTV backfill — walks every traveller server-side and
  // persists computed `totalBookingsValue`. Fixes the legacy "everyone
  // showing zero" issue without forcing the admin to open each profile.
  const handleRecalcAllLtv = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Recalculate LTV for ALL travellers? This rebuilds totalBookingsValue from trip history. Manual overrides are preserved.')) return;
    setRecalcingAll(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/clients/recalc-all-ltv`, {}, { withCredentials: true });
      const p = r.data?.processed || 0;
      const f = r.data?.failed || 0;
      toast.success(`Recalculated ${p} traveller${p === 1 ? '' : 's'}${f ? ` · ${f} failed` : ''}`);
      // Refresh the list so the new values render.
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const refreshed = await axios.get(`${BACKEND_URL}/api/clients${q}`, { withCredentials: true });
      setRows(Array.isArray(refreshed.data) ? refreshed.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Recalc failed');
    } finally {
      setRecalcingAll(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="travellers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Travellers</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Loading…' : `${rows.length} on file`}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              data-testid="toggle-archived-travellers-btn"
              onClick={() => setShowDeleted((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                showDeleted ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {showDeleted ? 'Showing archived' : 'Show archived'}
            </button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalcAllLtv}
              disabled={recalcingAll}
              data-testid="travellers-recalc-all-ltv-btn"
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${recalcingAll ? 'animate-spin' : ''}`} />
              {recalcingAll ? 'Recalculating…' : 'Recalc all LTV'}
            </Button>
          )}
          {canWrite && (
          <Link to="/clients/new" data-testid="new-traveller-btn">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" size="sm">
              <UserPlus className="h-4 w-4 mr-1" /> Add traveller
            </Button>
          </Link>
          )}
          {isAdmin && <ExportButton filename="travellers" columns={exportColumns} rows={sorted}
            fetchRows={async () => {
              const params = new URLSearchParams();
              if (search) params.set('search', search);
              const res = await axios.get(`${BACKEND_URL}/api/clients/export?${params.toString()}`, { withCredentials: true });
              const byUcc = {};
              for (const r of res.data) byUcc[r.ucc] = r;
              return sorted.map((r) => ({ ...r, ...(byUcc[r.ucc] || {}) }));
            }}
            testid="travellers-export" />}
        </div>
      </div>

      {/* Search + chips toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative" style={{ width: 280 }}>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, UCC, phone…"
            className="pl-8 h-9 rounded-[7px]"
            data-testid="travellers-search"
          />
        </div>
        <FilterChips chips={chips} value={categoryFilter} onChange={setCategoryFilter} testidPrefix="cat-chip" />
      </div>

      <BulkBar
        count={selected.size}
        actions={[
          { key: 'tag', label: 'Tag' },
          { key: 'email', label: 'Email' },
          ...(canWrite ? [{ key: 'delete', label: 'Delete', tone: 'danger', icon: Trash2 }] : []),
        ]}
        onAction={onBulkAction}
        onClear={() => setSelected(new Set())}
        testid="travellers-bulk-bar"
      />

      <TableShell
        pageRows={pageRows}
        loading={loading}
        sort={sort} setSort={setSort}
        selected={selected} toggle={toggle} toggleAll={toggleAll}
        onRowDelete={onRowDelete}
        onRowRestore={onRowRestore}
        showDeleted={showDeleted}
        navigate={navigate}
      />

      <DocLegend />

      <Pagination
        page={page} pageSize={pageSize} total={total}
        onPageChange={setPage} onPageSizeChange={setPageSize}
        testid="travellers-pagination"
      />
    </div>
  );
};

const TableShell = ({ pageRows, loading, sort, setSort, selected, toggle, toggleAll, onRowDelete, onRowRestore, showDeleted, navigate }) => {
  const allChecked = pageRows.length > 0 && pageRows.every((r) => selected.has(r.ucc));
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="travellers-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="text-left">
              <th className="w-10 px-3 py-2">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="check-all" />
              </th>
              <th className="px-3 py-2"><SortableHeader label="Name" columnKey="name" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2"><SortableHeader label="UCC" columnKey="ucc" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</th>
              <th className="px-3 py-2 text-right"><SortableHeader label="LTV" columnKey="ltv" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Trips" columnKey="trips" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2"><SortableHeader label="Last trip" columnKey="lastTrip" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Docs</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Phone</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400 text-xs">No travellers match the current filters.</td></tr>
            ) : (
              pageRows.map((r) => (
                <TravellerRow
                  key={r.ucc}
                  r={r}
                  checked={selected.has(r.ucc)}
                  toggle={toggle}
                  onDelete={onRowDelete}
                  onRestore={onRowRestore}
                  showDeleted={showDeleted}
                  navigate={navigate}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TravellerRow = ({ r, checked, toggle, onDelete, onRestore, showDeleted, navigate }) => {
  const tripsCount = Number.isFinite(r.tripCount) ? r.tripCount : (Array.isArray(r.trips) ? r.trips.length : 0);
  const ltv = Number(r.totalBookingsValue || 0);
  const avg = tripsCount > 0 ? Math.round(ltv / tripsCount) : 0;
  const lastTripDest = (Array.isArray(r.destinationsCovered) && r.destinationsCovered.length > 0)
    ? r.destinationsCovered[r.destinationsCovered.length - 1] : null;

  // Doc dot computation. Passport: green if expiry > 6 months, amber if missing,
  // red if expiring within 6 months, gray if no expiry.
  const passportMonths = monthsUntil(r.passportExpiryDate);
  let passportTone = 'gray';
  if (r.passportNumber || r.hasPassport) {
    if (passportMonths === null) passportTone = 'gray';
    else if (passportMonths < 0) passportTone = 'red';
    else if (passportMonths <= 6) passportTone = 'red';
    else passportTone = 'green';
  } else passportTone = 'amber';

  const aadharTone = (r.aadharCard || r.hasAadhaar) ? 'green' : 'amber';
  // Visa doc: clients schema doesn't carry a unified flag; treat any
  // non-empty `passportIssuedAt` country (international travellers) as
  // hint. Otherwise gray (not uploaded).
  const visaTone = r.passportIssuedAt ? 'gray' : 'gray';

  const catClass = CATEGORY_COLORS[r.travelCategory] || CATEGORY_DEFAULT;
  const initials = (r.fullName || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <tr className="group hover:bg-slate-50" style={{ height: 44 }} data-testid={`traveller-row-${r.ucc}`}>
      <td className="px-3 py-1.5">
        <input type="checkbox" checked={checked} onChange={() => toggle(r.ucc)} data-testid={`check-${r.ucc}`} />
      </td>
      <td className="px-3 py-1.5">
        <Link to={`/clients/${r.ucc}`} className="flex items-center gap-2 hover:text-emerald-700">
          <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{r.fullName || '—'}</div>
            <div className="text-[10px] text-slate-400 truncate">{r.email || ''}</div>
          </div>
        </Link>
      </td>
      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700" data-testid={`ucc-${r.ucc}`}>{r.displayUCC || r.ucc}</td>
      <td className="px-3 py-1.5">
        {r.travelCategory ? (
          <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 ${catClass}`} data-testid={`category-${r.ucc}`}>
            {r.travelCategory}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 text-right">
        <div className="font-mono font-semibold text-emerald-700">{fmtINR(ltv)}</div>
        {avg > 0 ? <div className="text-[10px] text-slate-400 font-mono">avg {fmtINR(avg)}</div> : null}
      </td>
      <td className="px-3 py-1.5 text-right font-mono text-slate-700">{tripsCount}</td>
      <td className="px-3 py-1.5 text-slate-600">
        {lastTripDest || '—'} <span className="text-slate-400">· {fmtMonth(r.lastTravelDate)}</span>
      </td>
      <td className="px-3 py-1.5">
        <DocDots tones={[passportTone, visaTone, aadharTone]} testid={`docs-${r.ucc}`} />
      </td>
      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600">{r.phone || '—'}</td>
      <td className="px-3 py-1.5 text-right">
        <RowActions ucc={r.ucc} phone={r.phone} fullName={r.fullName} onDelete={onDelete} onRestore={onRestore} showDeleted={showDeleted} navigate={navigate} />
      </td>
    </tr>
  );
};

const DocDots = ({ tones, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {tones.map((tone, i) => (
      <span
        key={i}
        title={['Passport', 'Visa', 'Aadhar'][i] + ' · ' + tone}
        className={`h-2 w-2 rounded-full ${DOC_TONE[tone] || DOC_TONE.gray}`}
      />
    ))}
  </div>
);

const RowActions = ({ ucc, phone, fullName, onDelete, onRestore, showDeleted, navigate }) => {
  const { user } = useAuth();
  const canWrite = (user?.role || '').toLowerCase() !== 'accounts';
  const waUrl = phone
    ? `https://wa.me/${String(phone).replace(/\D/g, '')}?text=${encodeURIComponent('Hi ' + (fullName || ''))}`
    : null;
  if (showDeleted) {
    return (
      <div className="inline-flex items-center gap-0.5" data-testid={`row-actions-${ucc}`}>
        <ActionBtn title="Restore" testid={`restore-traveller-btn-${ucc}`} onClick={() => onRestore(ucc)}><RefreshCw className="h-3.5 w-3.5" /></ActionBtn>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`row-actions-${ucc}`}>
      <ActionBtn title="View" testid={`act-view-${ucc}`} onClick={() => navigate(`/clients/${ucc}`)}><Eye className="h-3.5 w-3.5" /></ActionBtn>
      {waUrl ? (
        <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid={`act-wa-${ucc}`}>
          <ActionBtn title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></ActionBtn>
        </a>
      ) : null}
      {canWrite && <ActionBtn title="New Trip" testid={`act-trip-${ucc}`} onClick={() => navigate(`/quotes/new?ucc=${ucc}`)}><Plus className="h-3.5 w-3.5" /></ActionBtn>}
      {canWrite && <ActionBtn title="Archive" testid={`archive-traveller-btn-${ucc}`} onClick={() => onDelete(ucc)} tone="danger"><Trash2 className="h-3.5 w-3.5" /></ActionBtn>}
    </div>
  );
};

const ActionBtn = ({ children, onClick, title, testid, tone }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`h-[26px] w-[26px] inline-flex items-center justify-center rounded-[6px] transition-colors ${
      tone === 'danger' ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-600 hover:bg-slate-100'
    }`}
  >
    {children}
  </button>
);

const DocLegend = () => (
  <div className="flex items-center gap-4 text-[10px] text-slate-500 px-1" data-testid="doc-legend">
    <span>Docs:</span>
    <LegendDot tone="green" label="Valid" />
    <LegendDot tone="amber" label="Missing" />
    <LegendDot tone="red" label="Expiring < 6 mo" />
    <LegendDot tone="gray" label="Not uploaded" />
  </div>
);

const LegendDot = ({ tone, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${DOC_TONE[tone]}`} />
    {label}
  </span>
);

export default TravellersList;
